import { useEffect, useState } from 'react';
import { api, formatEdition, formatSize, loadWithRetry, peek } from './api';
import { matchesArtwork, matchesCategory } from './search';
import { LoadingSpinner, Skeleton, toast } from './feedback';
import SearchBar from './SearchBar';
import AddCategoryDialog from './AddCategoryDialog';
import ArtworkForm from './ArtworkForm';
import RenameDialog from './RenameDialog';
import DeleteCategoryDialog from './DeleteCategoryDialog';
import TrashDialog from './TrashDialog';
import CategoryImageDialog from './CategoryImageDialog';

export const UNCATEGORIZED = 'none'; // thư mục "Chưa phân loại": tranh không thuộc danh mục nào

// Cột của bảng tranh; bấm tiêu đề cột để sắp xếp theo cột đó
const COLUMNS = [
  { key: 'code', label: 'Mã chứng nhận', value: (a) => a.code || '' },
  { key: 'edition', label: 'Phiên bản', value: (a) => a.editionNo ?? Infinity },
  { key: 'size', label: 'Kích thước', value: (a) => a.size || '' },
  { key: 'owner', label: 'Chủ sở hữu', value: (a) => a.ownerName || '' },
];
const QR_FILTERS = [
  { key: 'all', label: 'Tất cả', test: () => true },
  { key: 'qr', label: 'Đã xuất QR', test: (a) => !!a.qrToken },
  { key: 'noqr', label: 'Chưa xuất QR', test: (a) => !a.qrToken },
];

function sortArtworks(list, { key, dir }) {
  const column = COLUMNS.find((c) => c.key === key);
  const compare = (x, y) => {
    const a = column.value(x);
    const b = column.value(y);
    return typeof a === 'number' ? a - b : String(a).localeCompare(String(b), 'vi', { numeric: true });
  };
  return [...list].sort((x, y) => compare(x, y) * dir);
}

// Khung xương lúc tải: cùng bố cục với trang thật (trang chủ: lưới thư mục; trang danh mục: bảng tranh)
function GallerySkeleton({ inFolder, waking }) {
  return (
    <section aria-busy="true" aria-label="Đang tải">
      <LoadingSpinner />
      {waking && <p className="waking-note">Máy chủ đang khởi động, vui lòng đợi trong giây lát…</p>}
      {inFolder ? (
        <Skeleton width={110} height={38} radius={8} style={{ marginBottom: 16 }} />
      ) : (
        <div className="home-toolbar">
          <Skeleton width={420} height={42} radius={999} />
        </div>
      )}
      <div className="category-panel-head">
        <Skeleton width={180} height={26} />
        <div className="category-panel-actions">
          <Skeleton width={130} height={36} radius={8} />
          <Skeleton width={100} height={36} radius={8} />
        </div>
      </div>
      {inFolder ? (
        <div className="art-list">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="art-row art-skeleton">
              {[110, 50, 60, 100].map((w, j) => (
                <span key={j} className="art-cell">
                  <Skeleton width={w} height={14} />
                </span>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="folder-grid">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="folder-card folder-skeleton">
              <Skeleton width="70%" height={16} />
              <Skeleton width="30%" height={12} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// folder: không có = trang chủ (lưới thư mục danh mục); id danh mục hoặc UNCATEGORIZED = trang của danh mục đó
export default function GalleryPage({
  categories, // null = đang tải; mỗi danh mục có artworkCount do máy chủ đếm
  stats, // { total, withQr, trash, uncategorized }
  materials = [],
  waking,
  folder,
  onChanged,
  setCategories,
  error,
  query,
  onQueryChange,
  onOpen,
  onOpenFolder,
}) {
  const [dialog, setDialog] = useState(null); // 'addCategory' | 'create' | 'image' | 'rename' | 'delete' | 'trash'
  const [folderQuery, setFolderQuery] = useState(''); // tìm kiếm trong trang danh mục
  const [sort, setSort] = useState({ key: 'code', dir: 1 }); // sắp xếp bảng tranh (dir 1 = tăng dần)
  const [qrFilter, setQrFilter] = useState('all');
  const [picked, setPicked] = useState(null); // null = không ở chế độ chọn; Set id tranh đang chọn để xoá
  const [deleting, setDeleting] = useState(false);

  // Trang danh mục: chỉ tải tranh của đúng danh mục đang mở
  const inFolder = folder !== undefined;
  const [folderArtworks, setFolderArtworks] = useState(null); // null = đang tải
  const [folderError, setFolderError] = useState('');
  const [folderWaking, setFolderWaking] = useState(false);
  const loadFolder = () =>
    loadWithRetry(() => api.getFolder(folder), () => setFolderWaking(true))
      .then((list) => {
        setFolderArtworks(list);
        setFolderError('');
      })
      .catch(() => setFolderError('Không kết nối được máy chủ. Vui lòng tải lại trang sau ít phút.'))
      .finally(() => setFolderWaking(false));
  useEffect(() => {
    if (!inFolder) return;
    const cached = peek(() => api.getFolder(folder)); // mở lại danh mục đã xem: hiện ngay bản đã lưu
    if (cached) setFolderArtworks(cached);
    loadFolder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder]);

  // Số tranh của một thư mục (máy chủ đếm sẵn)
  const countOf = (key) => (key === UNCATEGORIZED ? stats?.uncategorized ?? 0 : categories?.find((c) => c.id === key)?.artworkCount ?? 0);

  // Danh mục đang mở (null ở trang chủ hoặc "Chưa phân loại"); các nút Tạo tranh/Đổi ảnh/... tác động lên nó
  const scope = (categories || []).find((c) => c.id === folder);

  const closeDialog = () => setDialog(null);
  // Sau mỗi thay đổi: chỗ vừa sửa đã được cập nhật ngay trên màn hình; danh mục, thống kê và tranh
  // của danh mục đang mở được lấy lại ngầm từ máy chủ cho khớp (không bắt người dùng chờ)
  const reload = () => {
    onChanged();
    if (inFolder) loadFolder();
  };
  const refresh = () => {
    setDialog(null);
    reload();
  };
  const dropCategory = (id) => setCategories((list) => list.filter((c) => c.id !== id));

  // Danh mục không có tranh thì xoá luôn; còn tranh thì hỏi chuyển tranh đi đâu.
  // Tranh trong thùng rác (nếu có) thành không có danh mục.
  const removeCategory = async () => {
    if ((folderArtworks || []).length > 0) {
      setDialog('delete');
      return;
    }
    try {
      await api.deleteCategory(scope.id, null);
      toast(`Đã xoá danh mục "${scope.name}"`);
      dropCategory(scope.id);
      onOpenFolder(null);
      onChanged();
    } catch (err) {
      toast(`Xoá lỗi: ${err.message}`);
    }
  };

  const togglePick = (id) => {
    const next = new Set(picked);
    next.has(id) ? next.delete(id) : next.add(id);
    setPicked(next);
  };

  // Xoá nhiều tranh: giống xoá từng tranh — chưa xuất QR thì xoá hẳn, đã xuất QR thì vào thùng rác
  const removePicked = async () => {
    const list = folderArtworks.filter((a) => picked.has(a.id));
    const withQr = list.filter((a) => a.qrToken).length;
    const message =
      `Xoá ${list.length} tranh đã chọn?\n\n` +
      [
        list.length - withQr > 0 && `• ${list.length - withQr} tranh chưa xuất QR sẽ bị xoá vĩnh viễn.`,
        withQr > 0 && `• ${withQr} tranh đã xuất QR sẽ vào Thùng rác (quét QR sẽ thấy "đã bị thu hồi").`,
      ]
        .filter(Boolean)
        .join('\n');
    if (!window.confirm(message)) return;
    setDeleting(true);
    let failed = 0;
    const removed = new Set();
    for (const a of list) {
      try {
        if (a.qrToken) await api.remove(a.id);
        else await api.removePermanently(a.id);
        removed.add(a.id);
      } catch {
        failed++;
      }
    }
    setDeleting(false);
    setPicked(null);
    setFolderArtworks((all) => all.filter((a) => !removed.has(a.id)));
    toast(failed ? `Đã xoá ${list.length - failed} tranh, ${failed} tranh lỗi` : `Đã xoá ${list.length} tranh`);
    reload();
  };

  // Một dòng tranh (không có ảnh): mã chứng nhận, phiên bản, kích thước, chủ sở hữu
  const renderRow = (a) => {
    const isPicked = picked?.has(a.id);
    return (
      <button
        key={a.id}
        className={`art-row ${isPicked ? 'picked' : ''}`}
        aria-pressed={picked ? isPicked : undefined}
        onClick={() => (picked ? togglePick(a.id) : onOpen(a))}
      >
        {picked && <span className="row-check">{isPicked ? '✓' : ''}</span>}
        <span className="art-cell art-code" data-label="Mã chứng nhận">{a.code}</span>
        <span className="art-cell" data-label="Phiên bản">{formatEdition(a) || '—'}</span>
        <span className="art-cell" data-label="Kích thước">{formatSize(a.size) || '—'}</span>
        <span className="art-cell" data-label="Chủ sở hữu">{a.ownerName || '—'}</span>
      </button>
    );
  };

  const renderList = (list) => (
    <div className={`art-list ${picked ? 'picking' : ''}`}>
      <div className="art-row art-head">
        {COLUMNS.map((c) => (
          <button
            key={c.key}
            className={`art-sort ${sort.key === c.key ? 'active' : ''}`}
            onClick={() => setSort({ key: c.key, dir: sort.key === c.key ? -sort.dir : 1 })}
            aria-label={`Sắp xếp theo ${c.label}`}
          >
            {c.label}
            <span className="art-sort-arrow">{sort.key === c.key ? (sort.dir === 1 ? '▲' : '▼') : '↕'}</span>
          </button>
        ))}
      </div>
      {sortArtworks(list, sort).map(renderRow)}
    </div>
  );

  // Ô thư mục ở trang chủ: tên danh mục + tổng số tranh
  const renderFolder = (key, name) => (
    <button key={key} className="folder-card" onClick={() => onOpenFolder(key)}>
      <strong className="folder-name">{name}</strong>
      <span className="folder-count">{countOf(key)} tranh</span>
    </button>
  );

  if (error || folderError) return <p className="error">{error || folderError}</p>;
  if (!categories || !stats || (inFolder && !folderArtworks)) return <GallerySkeleton inFolder={inFolder} waking={waking || folderWaking} />;

  const trashDialog = dialog === 'trash' && <TrashDialog onRestored={reload} onClose={closeDialog} />;

  // ---------- Trang chủ: lưới thư mục, ô tìm kiếm chỉ lọc theo tên danh mục ----------
  if (!inFolder) {
    const searching = query.trim() !== '';
    const folders = [
      ...categories.map((c) => ({ key: c.id, name: c.name })),
      ...(stats.uncategorized > 0 ? [{ key: UNCATEGORIZED, name: 'Chưa phân loại' }] : []),
    ];
    const shownFolders = searching ? folders.filter((f) => matchesCategory(f, query)) : folders;
    return (
      <section>
        <div className="home-toolbar">
          <SearchBar
            items={folders}
            matches={matchesCategory}
            describe={(f) => ({ key: f.key, title: f.name, sub: `${countOf(f.key)} tranh` })}
            query={query}
            onQueryChange={onQueryChange}
            onPick={(f) => onOpenFolder(f.key)}
            placeholder="Tìm danh mục"
            emptyText="Không tìm thấy danh mục phù hợp"
          />
        </div>

        {/* Dải thống kê nhanh */}
        <div className="stats">
          <div className="stat stat-blue">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Tổng số tranh</span>
          </div>
          <div className="stat stat-orange">
            <span className="stat-value">{stats.withQr}</span>
            <span className="stat-label">Đã xuất QR</span>
          </div>
          <div className="stat stat-teal">
            <span className="stat-value">{categories.length}</span>
            <span className="stat-label">Danh mục</span>
          </div>
          <button className="stat stat-purple" onClick={() => setDialog('trash')}>
            <span className="stat-value">{stats.trash}</span>
            <span className="stat-label">Trong thùng rác</span>
          </button>
        </div>

        <div className="category-panel-head">
          <h2>
            Danh mục <span className="sidebar-count">{shownFolders.length}</span>
          </h2>
          <div className="category-panel-actions">
            <button className="primary" onClick={() => setDialog('addCategory')}>
              + Thêm danh mục
            </button>
            <button onClick={() => setDialog('trash')}>Thùng rác</button>
          </div>
        </div>

        {folders.length === 0 ? (
          <p className="muted category-empty">Chưa có danh mục nào. Bấm "+ Thêm danh mục" để bắt đầu.</p>
        ) : shownFolders.length === 0 ? (
          <p className="muted category-empty">Không tìm thấy danh mục phù hợp.</p>
        ) : (
          <div className="folder-grid">{shownFolders.map((f) => renderFolder(f.key, f.name))}</div>
        )}

        {dialog === 'addCategory' && (
          <AddCategoryDialog
            onDone={({ category, imageError }) => {
              toast(`Đã thêm danh mục "${category.name}"`);
              if (imageError) toast(`Lỗi ảnh: ${imageError}`);
              setCategories((list) => [...list, { ...category, artworkCount: 0 }]);
              refresh();
            }}
            onCancel={closeDialog}
          />
        )}
        {trashDialog}
      </section>
    );
  }

  // ---------- Trang của một danh mục: bảng tranh + các nút quản lý ----------
  const back = (
    <button className="back" onClick={() => onOpenFolder(null)}>
      ← Danh mục
    </button>
  );
  if (folder !== UNCATEGORIZED && !scope) {
    return (
      <section>
        {back}
        <p className="muted">Không tìm thấy danh mục này.</p>
      </section>
    );
  }

  const inThisFolder = folderArtworks;
  const qrTest = QR_FILTERS.find((f) => f.key === qrFilter).test;
  const shown = inThisFolder.filter((a) => qrTest(a) && (!folderQuery.trim() || matchesArtwork(a, folderQuery)));
  // Số bản tiếp theo trong danh mục, điền sẵn vào form (sửa được)
  const nextEdition = scope ? Math.max(0, ...inThisFolder.map((a) => a.editionNo || 0)) + 1 : 1;

  // Tạo tranh từ form đầy đủ; ảnh danh mục do backend copy thành ảnh của tranh
  const createArtwork = async (data) => {
    const created = await api.create(data);
    toast(`Đã tạo tranh ${created.code}`);
    setFolderArtworks((all) => [...all, created]);
    refresh();
  };

  return (
    <section>
      <div className="home-toolbar">
        {back}
        {/* Tìm trong danh mục: theo mã chứng nhận, chủ sở hữu, kích thước */}
        <SearchBar
          items={inThisFolder}
          matches={matchesArtwork}
          describe={(a) => ({
            key: a.id,
            title: a.code,
            sub: [a.ownerName, formatSize(a.size), formatEdition(a) && `Bản ${formatEdition(a)}`].filter(Boolean).join(' · '),
          })}
          query={folderQuery}
          onQueryChange={setFolderQuery}
          onPick={onOpen}
          placeholder="Tìm mã chứng nhận, chủ sở hữu, kích thước"
          emptyText="Không tìm thấy tranh phù hợp"
        />
      </div>
      <div className="category-panel-head">
        <h2>
          {scope ? scope.name : 'Chưa phân loại'} <span className="sidebar-count">{inThisFolder.length}</span>
        </h2>
        <div className="category-panel-actions">
          {picked ? (
            <>
              <span className="pick-count">Đã chọn {picked.size} tranh</span>
              <button onClick={() => setPicked(null)} disabled={deleting}>
                Huỷ
              </button>
              <button className="danger" onClick={removePicked} disabled={deleting || picked.size === 0}>
                {deleting ? 'Đang xoá...' : `Xoá ${picked.size} tranh`}
              </button>
            </>
          ) : (
            <>
              {scope && (
                <>
                  <button className="primary" onClick={() => setDialog('create')}>
                    + Tạo tranh
                  </button>
                  <button onClick={() => setDialog('image')}>Đổi ảnh</button>
                  <button onClick={() => setDialog('rename')}>Đổi tên</button>
                  <button className="danger" onClick={removeCategory}>
                    Xoá danh mục
                  </button>
                </>
              )}
              {inThisFolder.length > 0 && (
                <button className="danger" onClick={() => setPicked(new Set())}>
                  Xoá tranh
                </button>
              )}
              <button onClick={() => setDialog('trash')}>Thùng rác</button>
            </>
          )}
        </div>
      </div>

      {inThisFolder.length > 0 && (
        <div className="chips" role="group" aria-label="Lọc theo QR">
          {QR_FILTERS.map((f) => (
            <button key={f.key} className={`chip ${qrFilter === f.key ? 'active' : ''}`} onClick={() => setQrFilter(f.key)}>
              {f.label} <span>{inThisFolder.filter(f.test).length}</span>
            </button>
          ))}
        </div>
      )}

      {shown.length === 0 ? (
        <p className="muted category-empty">
          {inThisFolder.length > 0
            ? 'Không tìm thấy tranh phù hợp.'
            : scope
              ? 'Chưa có tranh nào. Bấm "+ Tạo tranh" để thêm.'
              : 'Chưa có tranh nào.'}
        </p>
      ) : (
        renderList(shown)
      )}

      {dialog === 'create' && scope && (
        <div className="dialog-backdrop" onClick={closeDialog}>
          <div className="form-dialog" onClick={(e) => e.stopPropagation()}>
            <ArtworkForm
              heading={`Thêm tranh vào "${scope.name}"`}
              initial={{ title: scope.name, categoryId: String(scope.id), editionNo: nextEdition, material: 'In canvas cao cấp' }}
              categories={categories}
              materials={materials}
              onSave={createArtwork}
              onCancel={closeDialog}
            />
          </div>
        </div>
      )}
      {dialog === 'image' && scope && (
        <CategoryImageDialog
          category={scope}
          onDone={() => {
            toast('Đã đổi ảnh danh mục');
            refresh();
          }}
          onCancel={closeDialog}
        />
      )}
      {dialog === 'rename' && scope && (
        <RenameDialog
          title={`Đổi tên "${scope.name}"`}
          initial={scope.name}
          onSubmit={async (name) => {
            const renamed = await api.renameCategory(scope.id, { name });
            toast('Đã đổi tên danh mục');
            setCategories((list) => list.map((c) => (c.id === renamed.id ? { ...c, name: renamed.name } : c)));
            refresh();
          }}
          onCancel={closeDialog}
        />
      )}
      {dialog === 'delete' && scope && (
        <DeleteCategoryDialog
          category={scope}
          categories={categories}
          onConfirm={async (targetId) => {
            try {
              await api.deleteCategory(scope.id, targetId);
              toast(targetId === 'trash' ? `Đã xoá "${scope.name}", tranh đã vào thùng rác` : `Đã xoá danh mục "${scope.name}"`);
              dropCategory(scope.id);
              onOpenFolder(null);
              onChanged();
            } catch (err) {
              toast(`Xoá lỗi: ${err.message}`);
              closeDialog();
            }
          }}
          onCancel={closeDialog}
        />
      )}
      {trashDialog}
    </section>
  );
}
